import { Box, Center, Flex, Heading, Image, Link, Text } from '@chakra-ui/react';
import { useKeycloak } from '@react-keycloak/web';
import { useContext, useEffect, useState } from 'react';
import { BrowserView } from 'react-device-detect';
import { Link as ReactRouterLink, useParams } from 'react-router-dom';
import { AuthenticatedKeycloak, isAuthenticated } from '../auth/keycloak';
import { GameSocials } from '../component/gameComponents/GameSocials';
import { GameStatCounts } from '../component/gameComponents/GameStatCounts';
import { BrowserSidebar } from '../component/globalComponents/BrowserSidebar';
import { BrowserTopBar } from '../component/globalComponents/BrowserTopBar';
import { MobileViewPage } from '../component/globalComponents/MobileViewPage';
import { GameLeaderBoard } from '../component/leaderboardComponents/GameLeaderboard';
import { Currency } from '../component/profileComponents/Currency';
import { GameProductList } from '../component/storeComponents/GameProductList';
import PageDataContext from '../context/PageDataContext';
import { usePageDataCache } from '../hooks/usePageDataCache';
import { usePageSessionStorage } from '../hooks/usePageSessionStorage';
import {
    BINGO_LICENSE,
    getGameInfo,
    LEVIATHAN_LICENSE,
    LocalGameInfo,
    MARBLES_LICENSE,
} from '../service/games';
import { getRoomsStats } from '../service/lobby';

export const GamePage = () => {
    const { keycloak } = useKeycloak();

    const { gameID } = useParams();

    const [refreshFlag, setRefresh] = useState<boolean>(false);
    const [streamGold, setStreamGold] = useState<number>(0);

    const [pageLoading, setPageLoading] = useState<boolean>(true);
    const [productsLoading, setProductsLoading] = useState<boolean>(true);

    const { isLoadFromCache } = useContext(PageDataContext);
    const [games, setGames] = usePageDataCache<LocalGameInfo[]>('games', []);
    const [game, setGame] = usePageDataCache<LocalGameInfo>('game', {
        blurb: '',
        deeplink: '',
        downloadLink: '',
        header: '',
        id: '',
        name: '',
        websites: [],
    });

    //Note: This requires https to work properly
    const shareSupported = navigator.share !== undefined;

    const [numGames, setNumGames] = usePageSessionStorage<number>('numGames', 0);
    const [numPlayers, setNumPlayers] = usePageSessionStorage<number>('numPlayers', 0);

    const handleShare = async (title: string, text: string, url: string) => {
        if (game === undefined) {
            console.error('handleShare - game data undefined');
            return;
        }

        try {
            const shareData = {
                title: title,
                text: text,
                url: url,
            };

            await navigator.share(shareData);
        } catch (error) {
            console.error(error);
        }
    };

    useEffect(() => {
        const setup = async () => {
            const games = await getGameInfo();
            setGames(games);
            if (gameID) {
                const game = games.find((x) => x.id === gameID);

                if (!!game) {
                    setGame(game);
                    if (game.id !== BINGO_LICENSE && game.id !== LEVIATHAN_LICENSE) {
                        setProductsLoading(false);
                    }
                }

                const stats = await getRoomsStats(gameID);
                if (!!stats) {
                    setNumGames(stats.totalSessions);
                    setNumPlayers(stats.totalPlayers);
                }
            }
            setPageLoading(false);
        };
        if (!isLoadFromCache) {
            setup();
        } else {
            setPageLoading(false);
        }
    }, []);

    const renderShopSection = (keycloak: AuthenticatedKeycloak) => {
        return (
            <>
                <Currency
                    keycloak={keycloak}
                    refreshFlag={refreshFlag}
                    quantityCallback={(qty) => {
                        setStreamGold(qty);
                    }}
                />
                <Flex className="divider" />
                <GameProductList
                    keycloak={keycloak}
                    license={game.id}
                    streamGoldBalance={streamGold}
                    onProductsLoaded={() => setProductsLoading(false)}
                    onPurchase={(success: boolean) => {
                        if (success) {
                            setRefresh(true);
                        }
                    }}
                />
                <Flex className="divider" />
            </>
        );
    };

    return (
        <Box>
            {game && (
                <>
                    <BrowserView>
                        <BrowserTopBar
                            isLoading={pageLoading || productsLoading}
                            loadingColor={'#feca3c'}
                        >
                            <Flex className={'sixes-title-browser browser-view-title'}>
                                <Text>SIXES</Text>
                            </Flex>
                        </BrowserTopBar>
                        <BrowserSidebar />
                        <Box className={'browser-content sixes-browser'}>
                            <Flex className={'game-header'}>
                                <Image src={game.header} className={'game-image'} />
                                <Flex className="game-data">
                                    <Flex className="game-title-socials">
                                        <Text className="game-title-browser">{game.name}</Text>
                                        <GameSocials websites={game.websites} />
                                    </Flex>
                                    <GameStatCounts
                                        gamesCount={numGames}
                                        playersCount={numPlayers}
                                    />
                                    <Text className="game-blurb-text">{game.blurb}</Text>
                                    <Flex
                                        className="button-container download-button"
                                        as={Link}
                                        href={game.downloadLink}
                                        isExternal
                                    >
                                        Download
                                    </Flex>
                                    {shareSupported && (
                                        <Flex
                                            className="button-container share-button"
                                            as={Link}
                                            onClick={() =>
                                                handleShare(
                                                    game.name,
                                                    game.blurb,
                                                    game.downloadLink,
                                                )
                                            }
                                        >
                                            Share
                                        </Flex>
                                    )}
                                    <Flex
                                        className="button-container share-button"
                                        as={ReactRouterLink}
                                        to={`/hosts?gameFilter=${game.id}`}
                                    >
                                        View Games
                                    </Flex>
                                </Flex>
                            </Flex>
                            <Flex className="divider" />
                            {isAuthenticated(keycloak) &&
                                (game.id === LEVIATHAN_LICENSE || game.id === BINGO_LICENSE) &&
                                renderShopSection(keycloak)}
                            <Flex className={'leaderboard-heading'}>
                                <Text className="game-leaderboard-title">Leaderboards</Text>
                                {shareSupported && (
                                    <Flex
                                        className="button-container share-button"
                                        as={Link}
                                        onClick={() =>
                                            handleShare(
                                                game.name,
                                                'Leaderboard',
                                                window.location.href,
                                            )
                                        }
                                    >
                                        Share
                                    </Flex>
                                )}
                            </Flex>
                            <Box className="game-leaderboard-holder">
                                {game.id === MARBLES_LICENSE ? (
                                    <Heading className={'leaderboard-coming-soon'}>
                                        <Center>Coming Soon!</Center>
                                    </Heading>
                                ) : (
                                    <GameLeaderBoard gameId={game.id} />
                                )}
                            </Box>
                        </Box>
                    </BrowserView>
                    <MobileViewPage
                        title="SIXES"
                        titleBackgroundColor="#feca3c"
                        titleColor="#000"
                        isLoading={pageLoading || productsLoading}
                    >
                        <Flex className={'mobile-content'}>
                            <Center>
                                <Image src={game.header} className={'game-image'} />
                            </Center>
                            <Flex className={'game-header'}>
                                <Flex className="game-data">
                                    <Text className="game-title">{game.name}</Text>
                                    <GameStatCounts
                                        gamesCount={numGames}
                                        playersCount={numPlayers}
                                    />
                                    <Text className="game-blurb-text">{game.blurb}</Text>
                                    <GameSocials websites={game.websites} />
                                    <Flex className="game-buttons">
                                        <Flex
                                            className="button-container download-button"
                                            as={Link}
                                            href={game.downloadLink}
                                            isExternal
                                        >
                                            Download
                                        </Flex>
                                        {shareSupported && (
                                            <Flex
                                                className="button-container share-button"
                                                as={Link}
                                                onClick={() =>
                                                    handleShare(
                                                        game.name,
                                                        game.blurb,
                                                        game.downloadLink,
                                                    )
                                                }
                                            >
                                                Share
                                            </Flex>
                                        )}
                                        <Flex
                                            className="button-container share-button"
                                            as={ReactRouterLink}
                                            to={`/hosts?gameFilter=${game.id}`}
                                        >
                                            View Games
                                        </Flex>
                                    </Flex>
                                </Flex>
                            </Flex>
                            <Flex className="divider" />
                            {isAuthenticated(keycloak) &&
                                (game.id === LEVIATHAN_LICENSE || game.id === BINGO_LICENSE) &&
                                renderShopSection(keycloak)}
                            <Flex className={'leaderboard-heading'}>
                                <Text className="game-leaderboard-title">Leaderboards</Text>
                                {shareSupported && (
                                    <Flex
                                        className="button-container share-button"
                                        as={Link}
                                        onClick={() =>
                                            handleShare(
                                                game.name,
                                                'Leaderboard',
                                                window.location.href,
                                            )
                                        }
                                    >
                                        Share
                                    </Flex>
                                )}
                            </Flex>
                            <Box className="game-leaderboard-holder">
                                {game.id === MARBLES_LICENSE ? (
                                    <Heading className={'leaderboard-coming-soon'}>
                                        <Center>Coming Soon!</Center>
                                    </Heading>
                                ) : (
                                    <GameLeaderBoard gameId={game.id} />
                                )}
                            </Box>
                        </Flex>
                    </MobileViewPage>
                </>
            )}
        </Box>
    );
};
